import { ReactNode, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  ListChecks,
  FileText,
  Wrench,
  Receipt,
  ScrollText,
  CalendarDays,
  Shield,
  Gavel,
  Wallet,
  DollarSign,
  AlertTriangle,
  Clock,
  ArrowUpDown,
} from "lucide-react";
import { api } from "@/lib/api";
import { Badge } from "@/components/Badge";
import { QueryError } from "@/components/QueryError";

type QueueType =
  | "WorksPermit"
  | "MaintenanceRequest"
  | "Expense"
  | "Resolution"
  | "MeetingMinutes"
  | "IncidentReport"
  | "LegalAction"
  | "Budget"
  | "MaintenanceFee";

type Health = "ok" | "attention" | "stalled" | "unknown";

interface QueueItem {
  id: string;
  type: QueueType;
  title: string;
  stage: string;
  ageInDays: number;
  ageBasis: "stage" | "created" | "dueDate";
  health: Exclude<Health, "unknown">;
  link: string;
}

interface QueueGroup {
  type: QueueType;
  activeCount: number;
  items: QueueItem[];
  health: Health;
  oldestActiveAgeInDays: number | null;
  completedLast7Days: number | null;
  completedLast30Days: number | null;
  throughputMeasurable: boolean;
  error?: true;
}

interface OperationsQueueResponse {
  generatedAt: string;
  summary: { totalActive: number; totalStalled: number; completedLast7Days: number };
  queues: QueueGroup[];
}

// Fixed display order — mirrors the backend's TYPE_ORDER so cards, filter
// chips, and the table stay in one predictable sequence across the page.
const QUEUE_TYPES: QueueType[] = [
  "WorksPermit",
  "MaintenanceRequest",
  "Expense",
  "Resolution",
  "MeetingMinutes",
  "IncidentReport",
  "LegalAction",
  "Budget",
  "MaintenanceFee",
];

// Card links go to each type's list page; per-item table links use the
// item's own `link` field instead (e.g. MeetingMinutes rows go to the
// specific meeting, not just the meetings list).
const TYPE_META: Record<QueueType, { icon: ReactNode; route: string }> = {
  WorksPermit: { icon: <FileText size={18} />, route: "/resident/works-permit" },
  MaintenanceRequest: { icon: <Wrench size={18} />, route: "/resident/maintenance" },
  Expense: { icon: <Receipt size={18} />, route: "/finance/expenses" },
  Resolution: { icon: <ScrollText size={18} />, route: "/resolutions" },
  MeetingMinutes: { icon: <CalendarDays size={18} />, route: "/meetings" },
  IncidentReport: { icon: <Shield size={18} />, route: "/admin/incidents" },
  LegalAction: { icon: <Gavel size={18} />, route: "/legal-actions" },
  Budget: { icon: <Wallet size={18} />, route: "/finance/budgets" },
  MaintenanceFee: { icon: <DollarSign size={18} />, route: "/finance/fees" },
};

const HEALTH_VARIANT: Record<Health, "success" | "warning" | "danger" | "default"> = {
  ok: "success",
  attention: "warning",
  stalled: "danger",
  unknown: "default",
};

const HEALTH_FILTERS: Exclude<Health, "unknown">[] = ["attention", "stalled"];

// Most stages already have a translated label somewhere else in the app —
// reused here instead of inventing parallel copy. Only Expense's status had
// no i18n key anywhere yet (finance.expenseStatus, added for this page).
// Every branch falls back to the raw stage string so an unexpected value
// never breaks the page.
function stageLabel(t: (key: string, opts?: Record<string, unknown>) => string, type: QueueType, stage: string): string {
  switch (type) {
    case "WorksPermit":
      return stage === "SUBMITTED" ? t("worksPermits.submitted") : t("worksPermits.under_review");
    case "MaintenanceRequest":
      return t(`maintenance.status.${stage}`, { defaultValue: stage });
    case "Expense":
      return t(`finance.expenseStatus.${stage}`, { defaultValue: stage });
    case "Resolution":
      return t("resolutions.voting");
    case "MeetingMinutes":
      return stage === "APPROVED" ? t("finance.budgetStatus.APPROVED") : t("worksPermits.under_review");
    case "IncidentReport":
      return t(`security.incidentStatus.${stage}`, { defaultValue: stage });
    case "LegalAction":
      return t(`legal.legalStatus.${stage}`, { defaultValue: stage });
    case "Budget":
      return t("finance.budgetStatus.DRAFT");
    case "MaintenanceFee":
      return stage === "OVERDUE" ? t("finance.overdue") : t("finance.partial");
    default:
      return stage;
  }
}

function chipClass(active: boolean): string {
  return `px-3 py-1 rounded-full text-sm transition ${
    active ? "bg-primary text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
  }`;
}

export function OperationsQueuePage() {
  const { t } = useTranslation();
  const [typeFilter, setTypeFilter] = useState<QueueType | "ALL">("ALL");
  const [healthFilter, setHealthFilter] = useState<Exclude<Health, "unknown"> | "ALL">("ALL");
  const [sortAsc, setSortAsc] = useState(false); // default: oldest first

  const { data, isLoading, isError } = useQuery({
    queryKey: ["operations-queue"],
    queryFn: async () => (await api.get<OperationsQueueResponse>("/operations-queue")).data,
  });

  const allItems = useMemo(() => {
    if (!data) return [];
    let items = data.queues.flatMap((q) => q.items);
    if (typeFilter !== "ALL") items = items.filter((i) => i.type === typeFilter);
    if (healthFilter !== "ALL") items = items.filter((i) => i.health === healthFilter);
    return [...items].sort((a, b) => (sortAsc ? a.ageInDays - b.ageInDays : b.ageInDays - a.ageInDays));
  }, [data, typeFilter, healthFilter, sortAsc]);

  if (isError) return <QueryError />;
  if (isLoading || !data) return <div className="text-slate-500">{t("common.loading")}</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <ListChecks size={24} /> {t("operationsQueue.title")}
        </h1>
        <p className="text-slate-600 mt-1">{t("operationsQueue.subtitle")}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="card flex items-center justify-between">
          <div>
            <div className="text-sm text-slate-500">{t("operationsQueue.summary.totalActive")}</div>
            <div className="text-2xl font-bold mt-1">{data.summary.totalActive}</div>
          </div>
          <ListChecks className="text-slate-500" />
        </div>
        <div className="card flex items-center justify-between">
          <div>
            <div className="text-sm text-slate-500">{t("operationsQueue.summary.totalStalled")}</div>
            <div className="text-2xl font-bold mt-1">{data.summary.totalStalled}</div>
          </div>
          <AlertTriangle className="text-red-500" />
        </div>
        <div className="card flex items-center justify-between">
          <div>
            <div className="text-sm text-slate-500">{t("operationsQueue.summary.completedLast7Days")}</div>
            <div className="text-2xl font-bold mt-1">{data.summary.completedLast7Days}</div>
          </div>
          <Clock className="text-green-500" />
        </div>
      </div>
      <p className="text-xs text-slate-500">{t("operationsQueue.summary.completedNote")}</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.queues.map((q) => {
          const meta = TYPE_META[q.type];
          return (
            <Link key={q.type} to={meta.route} className="card block hover:shadow-md transition">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 font-semibold">
                  {meta.icon} {t(`operationsQueue.types.${q.type}`)}
                </div>
                <Badge variant={HEALTH_VARIANT[q.health]}>{t(`operationsQueue.health.${q.health}`)}</Badge>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <div className="text-slate-500 text-xs">{t("operationsQueue.card.active")}</div>
                  <div className="font-medium">{q.activeCount}</div>
                </div>
                <div>
                  <div className="text-slate-500 text-xs">{t("operationsQueue.card.oldest")}</div>
                  <div className="font-medium">{q.oldestActiveAgeInDays != null ? `${q.oldestActiveAgeInDays}d` : "—"}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-slate-500 text-xs">{t("operationsQueue.card.completed7d")}</div>
                  <div className="font-medium">
                    {q.throughputMeasurable ? (
                      q.completedLast7Days
                    ) : (
                      <span className="text-slate-400 italic">{t("operationsQueue.card.notTracked")}</span>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      <div className="card">
        <h2 className="text-lg font-semibold mb-1">{t("operationsQueue.table.title")}</h2>
        <p className="text-xs text-slate-500 mb-3">{t("operationsQueue.table.capNote")}</p>

        <div className="space-y-2 mb-4">
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs font-semibold uppercase text-slate-500 mr-1">{t("operationsQueue.table.filterType")}</span>
            <button onClick={() => setTypeFilter("ALL")} className={chipClass(typeFilter === "ALL")}>
              {t("operationsQueue.table.all")}
            </button>
            {QUEUE_TYPES.map((qt) => (
              <button key={qt} onClick={() => setTypeFilter(qt)} className={chipClass(typeFilter === qt)}>
                {t(`operationsQueue.types.${qt}`)}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs font-semibold uppercase text-slate-500 mr-1">{t("operationsQueue.table.filterHealth")}</span>
            <button onClick={() => setHealthFilter("ALL")} className={chipClass(healthFilter === "ALL")}>
              {t("operationsQueue.table.all")}
            </button>
            {HEALTH_FILTERS.map((h) => (
              <button key={h} onClick={() => setHealthFilter(h)} className={chipClass(healthFilter === h)}>
                {t(`operationsQueue.health.${h}`)}
              </button>
            ))}
          </div>
        </div>

        {allItems.length === 0 ? (
          <p className="text-sm text-slate-500">{t("operationsQueue.table.empty")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3">{t("operationsQueue.table.columns.type")}</th>
                  <th className="py-2 pr-3">{t("operationsQueue.table.columns.description")}</th>
                  <th className="py-2 pr-3">{t("operationsQueue.table.columns.stage")}</th>
                  <th className="py-2 pr-3">
                    <button className="flex items-center gap-1 uppercase" onClick={() => setSortAsc((s) => !s)}>
                      {t("operationsQueue.table.columns.age")} <ArrowUpDown size={12} />
                    </button>
                  </th>
                  <th className="py-2 pr-3">{t("operationsQueue.table.columns.health")}</th>
                </tr>
              </thead>
              <tbody>
                {allItems.map((item) => (
                  <tr key={`${item.type}-${item.id}`} className="border-b border-slate-100 last:border-0">
                    <td className="py-2 pr-3">
                      <span className="inline-flex items-center gap-1 text-slate-600 whitespace-nowrap">
                        {TYPE_META[item.type].icon} {t(`operationsQueue.types.${item.type}`)}
                      </span>
                    </td>
                    <td className="py-2 pr-3">
                      <Link to={item.link} className="text-primary hover:underline">
                        {item.title}
                      </Link>
                    </td>
                    <td className="py-2 pr-3">{stageLabel(t, item.type, item.stage)}</td>
                    <td className="py-2 pr-3">
                      <div>{item.ageInDays}d</div>
                      <div className="text-xs text-slate-400">{t(`operationsQueue.ageBasis.${item.ageBasis}`)}</div>
                    </td>
                    <td className="py-2 pr-3">
                      <Badge variant={HEALTH_VARIANT[item.health]}>{t(`operationsQueue.health.${item.health}`)}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
